import { useState, useEffect, useCallback, useMemo, type Ref, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DateTime, Interval } from 'luxon';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import ShopSelector from '@/components/selectors/ShopSelector';
import ModuleSelector from '@/components/selectors/ModuleSelector';
import IntervalSelector from '@/components/selectors/IntervalSelector';
import DateSelector from '@/components/selectors/DateSelector';
import TimeSelector from '@/components/selectors/TimeSelector';
import type { ModuleType } from './types';
import { useStore } from '@core/store';
import { shallow } from 'zustand/shallow';

export interface DailyComparisonData {
    shopId1: string;
    deviceId1: string;
    moduleId1: string;
    shopId2: string;
    deviceId2: string;
    moduleId2: string;
    day1Start: string;
    day1End: string;
    day2Start: string;
    day2End: string;
    interval: number;
}

export interface FilterDailyComparisonProps {
    forType: 'trolleyShelter' | 'TrolleyShelter' | 'zoneTracker' | 'ZoneTracker' | 'lineTracker' | 'LineTracker';
    intervals?: number[];
    onLoadData: (data: DailyComparisonData) => void;
    ref?: Ref<FilterRef>;
}

export interface FilterRef {
    refresh: () => void;
}

const FilterDailyComparison = ({
    forType: forProp,
    intervals = [60, 30, 15, 10, 5],
    onLoadData,
    ref
}: FilterDailyComparisonProps) => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();

    const [shopId1, setShopId1] = useState<string | null>(null);
    const [moduleId1, setModuleId1] = useState<string | null>(null);
    const [shopId2, setShopId2] = useState<string | null>(null);
    const [moduleId2, setModuleId2] = useState<string | null>(null);

    const [day1, setDay1] = useState(DateTime.local().toISODate() ?? '');
    const [day2, setDay2] = useState(DateTime.local().toISODate() ?? '');
    const [startTime, setStartTime] = useState('06:00');
    const [endTime, setEndTime] = useState('22:00');
    const [interval, setInterval] = useState(60);

    const {
        trolleyShelterIds,
        zoneTrackerIds,
        lineTrackerIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds
    } = useStore(
        (state) => ({
            trolleyShelterIds: state.trolleyShelterIds,
            zoneTrackerIds: state.zoneTrackerIds,
            lineTrackerIds: state.lineTrackerIds,
            loadTrolleyShelterIds: state.loadTrolleyShelterIds,
            loadZoneTrackerIds: state.loadZoneTrackerIds,
            loadLineTrackerIds: state.loadLineTrackerIds
        }),
        shallow
    );

    useEffect(() => {
        const loadModuleData = async () => {
            const normalizedType = forProp.toLowerCase();
            if (normalizedType.includes('trolleyshelter') && (!trolleyShelterIds || trolleyShelterIds.length === 0)) {
                await loadTrolleyShelterIds();
            } else if (normalizedType.includes('zonetracker') && (!zoneTrackerIds || zoneTrackerIds.length === 0)) {
                await loadZoneTrackerIds();
            } else if (normalizedType.includes('linetracker') && (!lineTrackerIds || lineTrackerIds.length === 0)) {
                await loadLineTrackerIds();
            }
        };
        loadModuleData();
    }, [
        forProp,
        trolleyShelterIds,
        zoneTrackerIds,
        lineTrackerIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds
    ]);

    const getModuleList = useCallback(
        (shopId: string | null) => {
            if (!shopId) return [];
            const normalizedType = forProp.toLowerCase();
            let moduleList: any[] = [];
            if (normalizedType.includes('trolleyshelter')) moduleList = trolleyShelterIds;
            else if (normalizedType.includes('zonetracker')) moduleList = zoneTrackerIds;
            else if (normalizedType.includes('linetracker')) moduleList = lineTrackerIds;
            return moduleList.filter((m) => m.shopId === shopId && !m.disabled && !m.deleteDate);
        },
        [forProp, trolleyShelterIds, zoneTrackerIds, lineTrackerIds]
    );

    const filteredModuleIds1 = useMemo(() => getModuleList(shopId1), [shopId1, getModuleList]);
    const filteredModuleIds2 = useMemo(() => getModuleList(shopId2), [shopId2, getModuleList]);

    useEffect(() => {
        if (filteredModuleIds1.length > 0 && !moduleId1) setModuleId1(filteredModuleIds1[0].id);
        else if (filteredModuleIds1.length === 0 && moduleId1) setModuleId1(null);
    }, [filteredModuleIds1, moduleId1]);

    useEffect(() => {
        if (filteredModuleIds2.length > 0 && !moduleId2) setModuleId2(filteredModuleIds2[0].id);
        else if (filteredModuleIds2.length === 0 && moduleId2) setModuleId2(null);
    }, [filteredModuleIds2, moduleId2]);

    const selectedModule1 = useMemo(
        () => filteredModuleIds1.find((m) => m.id === moduleId1) || null,
        [filteredModuleIds1, moduleId1]
    );
    const selectedModule2 = useMemo(
        () => filteredModuleIds2.find((m) => m.id === moduleId2) || null,
        [filteredModuleIds2, moduleId2]
    );

    useEffect(() => {
        emitLoadData();
    }, [selectedModule1, selectedModule2, shopId1, shopId2, day1, day2, startTime, endTime, interval]);

    const availableIntervals = useMemo(() => {
        const diffHours = Interval.fromDateTimes(
            DateTime.fromISO(startTime, { zone: 'UTC' }),
            DateTime.fromISO(endTime, { zone: 'UTC' })
        ).length('hours');
        if (diffHours <= 1) return intervals;
        else if (diffHours > 1 && diffHours <= 3) return intervals.filter((i) => i >= 15);
        return intervals.filter((i) => i >= 30);
    }, [startTime, endTime, intervals]);

    const startTimeMax = useMemo(() => {
        const parts = endTime.split(':');
        if (parts.length < 2) return undefined;
        return DateTime.local()
            .set({ hour: parseInt(parts[0]), minute: parseInt(parts[1]) })
            .minus({ minutes: 5 });
    }, [endTime]);

    const endTimeMin = useMemo(() => {
        const parts = startTime.split(':');
        if (parts.length < 2) return undefined;
        return DateTime.local()
            .set({ hour: parseInt(parts[0]), minute: parseInt(parts[1]) })
            .plus({ minutes: 5 });
    }, [startTime]);

    const emitLoadData = useCallback(() => {
        if (!selectedModule1 || !selectedModule2 || !shopId1 || !shopId2) return;
        const startTimeParts = startTime.split(':');
        const endTimeParts = endTime.split(':');
        const startH = parseInt(startTimeParts[0], 10);
        const startM = parseInt(startTimeParts[1], 10);
        const endH = parseInt(endTimeParts[0], 10);
        const endM = parseInt(endTimeParts[1], 10);

        const day1Start = DateTime.fromISO(day1, { zone: 'UTC' }).set({ hour: startH, minute: startM }).toISO();
        const day1End = DateTime.fromISO(day1, { zone: 'UTC' }).set({ hour: endH, minute: endM }).toISO();
        const day2Start = DateTime.fromISO(day2, { zone: 'UTC' }).set({ hour: startH, minute: startM }).toISO();
        const day2End = DateTime.fromISO(day2, { zone: 'UTC' }).set({ hour: endH, minute: endM }).toISO();

        if (day1Start && day1End && day2Start && day2End) {
            onLoadData({
                shopId1: shopId1!,
                deviceId1: selectedModule1.deviceId,
                moduleId1: selectedModule1.id,
                shopId2: shopId2!,
                deviceId2: selectedModule2.deviceId,
                moduleId2: selectedModule2.id,
                day1Start,
                day1End,
                day2Start,
                day2End,
                interval
            });
        }
    }, [selectedModule1, selectedModule2, shopId1, shopId2, day1, day2, startTime, endTime, interval]);

    useImperativeHandle(ref, () => ({ refresh: () => emitLoadData() }), [emitLoadData]);

    useEffect(() => {
        const shopIdParam = searchParams.get('shopId');
        const moduleIdParam = searchParams.get('moduleId');
        if (shopIdParam) setShopId1(shopIdParam);
        if (moduleIdParam) setModuleId1(moduleIdParam);
    }, [searchParams]);

    const today = DateTime.local();

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 5,
                    mt: 1
                }}
            >
                {/* Row 1: Time pickers, Interval, and Load button */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                    <Box sx={{ flex: '0 0 auto' }}>
                        <TimeSelector
                            value={startTime}
                            onChange={setStartTime}
                            label={t('startTime')}
                            maxTime={startTimeMax}
                            minutesStep={5}
                        />
                    </Box>

                    <Box sx={{ flex: '0 0 auto' }}>
                        <TimeSelector
                            value={endTime}
                            onChange={setEndTime}
                            label={t('endTime')}
                            minTime={endTimeMin}
                            minutesStep={5}
                        />
                    </Box>

                    <Box sx={{ width: 240, flexShrink: 0 }}>
                        <IntervalSelector
                            value={interval}
                            onChange={setInterval}
                            intervals={availableIntervals}
                            unit="minutes"
                        />
                    </Box>
                </Box>

                {/* Row 2: Comparison fields */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* Group 1 */}
                    <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <Box>
                            <ShopSelector
                                value={shopId1}
                                onChange={(v) => {
                                    setShopId1(v);
                                    setModuleId1(null);
                                }}
                                label={`${t('shop')} 1`}
                            />
                        </Box>
                        <Box sx={{ width: 250 }}>
                            <ModuleSelector
                                value={moduleId1}
                                onChange={setModuleId1}
                                shopId={shopId1}
                                moduleType={forProp as ModuleType}
                                label={`${t(forProp)} 1`}
                                modules={filteredModuleIds1}
                            />
                        </Box>
                        <Box sx={{ width: 240 }}>
                            <DateSelector
                                value={day1 || null}
                                onChange={setDay1}
                                label={`${t('day')} 1`}
                                maxDate={today}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ flex: '1 1 auto' }} />

                    {/* Group 2 */}
                    <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <Box>
                            <ShopSelector
                                value={shopId2}
                                onChange={(v) => {
                                    setShopId2(v);
                                    setModuleId2(null);
                                }}
                                label={`${t('shop')} 2`}
                            />
                        </Box>
                        <Box sx={{ width: 250 }}>
                            <ModuleSelector
                                value={moduleId2}
                                onChange={setModuleId2}
                                shopId={shopId2}
                                moduleType={forProp as ModuleType}
                                label={`${t(forProp)} 2`}
                                modules={filteredModuleIds2}
                            />
                        </Box>
                        <Box sx={{ width: 240 }}>
                            <DateSelector
                                value={day2 || null}
                                onChange={setDay2}
                                label={`${t('day')} 2`}
                                maxDate={today}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default FilterDailyComparison;
